import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CreateFraudReviewRequest,
  FraudDetectionRequest,
  FraudDetectionResponse,
  FraudIcd10Code,
  FraudReviewRequest
} from '@secca/shared/models/atlas/atlas-fraud-detection.model';
import { Observable } from 'rxjs';
import { AtlasApiService } from './atlas-api.service';

@Injectable({
  providedIn: 'root'
})
export class AtlasFraudDetectionService {
  constructor(
    private http: HttpClient,
    private atlasApiService: AtlasApiService
  ) {}

  detectFraud(request: FraudDetectionRequest): Observable<FraudDetectionResponse> {
    const normalizedRequest = this.normalizeFraudDetectionRequest(request);
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.post<FraudDetectionResponse>(this.atlasApiService.buildUrl('/api/v1/fraud-detection'), normalizedRequest, { headers })
    );
  }

  getLatestFraudDetectionForCase(caseNumber: string): Observable<FraudDetectionResponse> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get<FraudDetectionResponse>(
        this.atlasApiService.buildUrl(`/api/v1/fraud-detection/case/${encodeURIComponent(caseNumber)}`),
        { headers }
      )
    );
  }

  getFraudReviewRequestForCase(caseNumber: string): Observable<FraudReviewRequest> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get<FraudReviewRequest>(
        this.atlasApiService.buildUrl(`/api/v1/fraud-detection/review-requests/case/${encodeURIComponent(caseNumber)}`),
        { headers }
      )
    );
  }

  createFraudReview(fraudReviewRequestId: number, request: CreateFraudReviewRequest): Observable<FraudReviewRequest> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.post<FraudReviewRequest>(
        this.atlasApiService.buildUrl(`/api/v1/fraud-detection/review-requests/${fraudReviewRequestId}/reviews`),
        request,
        { headers }
      )
    );
  }

  private normalizeFraudDetectionRequest(request: FraudDetectionRequest): FraudDetectionRequest {
    const normalizedRequest: FraudDetectionRequest = { ...request };
    const icd10 = this.normalizeFraudIcd10((request as { icd10?: FraudIcd10Code[] | string | string[] }).icd10);

    if (icd10.length) {
      normalizedRequest.icd10 = icd10;
    } else {
      delete normalizedRequest.icd10;
    }

    return normalizedRequest;
  }

  private normalizeFraudIcd10(icd10: FraudIcd10Code[] | string | string[] | null | undefined): FraudIcd10Code[] {
    const values = Array.isArray(icd10) ? icd10 : [icd10];
    const normalizedValues = values
      .map(value => {
        if (typeof value === 'string') {
          const code = value.trim();
          return code ? { code, source: 'Unknown' } : null;
        }

        const code = typeof value?.code === 'string' ? value.code.trim() : '';
        const source = typeof value?.source === 'string' && value.source.trim() ? value.source.trim() : 'Unknown';
        return code ? { code, source } : null;
      })
      .filter(Boolean) as FraudIcd10Code[];

    const seen = new Set<string>();
    return normalizedValues.filter(value => {
      const key = `${value.code.toLowerCase()}|${value.source.toLowerCase()}`;
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }
}
